import React from 'react';
import { useTranslation } from 'react-i18next';

import { apiErrorMessages, errorKind } from 'config/apiErrors';

interface FetchFailedProps {
  /** 取得に失敗したときのエラー（axios のエラーなど）。 */
  error: unknown;
  /** 同じ条件で取り直す。 */
  onRetry: () => void;
  /** 見出しの文（既定は「一覧を読み込めませんでした」）。 */
  message?: string;
  /** EV-318。狭い場所（左のメニューの下のプロフィールなど）に置くときの詰めた表示。文言は同じ。 */
  compact?: boolean;
  /** EV-322。欄の注記として使うときの id（欄の aria-describedby から結ぶ）。 */
  id?: string;
}

/**
 * ADM-15-AC1。一覧・詳細の取得に失敗したことを、空の一覧と区別して示す。
 *
 * 失敗の種類（通信断・権限・サーバー障害）を添え、権限が無いとき以外は再読み込みを出す
 * （権限が無いときは何度試しても同じ結果になるため）。
 */
const FetchFailed: React.FC<FetchFailedProps> = ({ error, onRetry, message, compact = false, id }) => {
  const { t } = useTranslation('common');
  const kind = errorKind(error);
  const reason =
    kind === 'network'
      ? t('ui.error-network', { ns: 'common' })
      : kind === 'forbidden'
      ? t('ui.error-forbidden', { ns: 'common' })
      : kind === 'server'
      ? t('ui.error-server', { ns: 'common' })
      : null;

  return (
    <div
      id={id}
      role="alert"
      className={
        compact
          ? 'flex flex-col items-center gap-1 p-2 text-caption text-gray-500'
          : 'flex flex-col items-center gap-2 py-8 text-gray-500'
      }
    >
      <p>{message ?? t('ui.fetch-failed', { ns: 'common' })}</p>
      {reason && <p>{reason}</p>}
      {kind === 'other' && apiErrorMessages(error, '').filter(Boolean).map((detail, index) => <p key={index}>{detail}</p>)}
      {kind !== 'forbidden' && (
        <button
          type="button"
          className={compact ? 'rounded-md border px-3 py-1 text-primary/300' : 'rounded-md border px-4 py-2 text-primary/300'}
          onClick={onRetry}
        >
          {t('ui.retry', { ns: 'common' })}
        </button>
      )}
    </div>
  );
};

export default FetchFailed;
